// src/components/auth/LoginModal.jsx
import * as React from 'react';
import {
  Dialog, DialogContent, Paper, Box, Typography, TextField, Button,
  Alert, Link, IconButton, Menu, MenuItem
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../lib/api';
import { saveAuth } from '../../lib/auth';
import FancyToast from '../common/FancyToast';

import { useI18n } from '../../i18n/I18nProvider';
import loginI18n from '../../i18n/pages/auth/login.i18n';

import { LANGS } from '../../i18n/langs';

const BLUE = '#0466c8';
const BLUE_HOVER = '#004890';

const textFieldSx = {
  '& label.Mui-focused': { color: BLUE },
  '& .MuiOutlinedInput-root': {
    '& fieldset': { borderColor: '#ccc' },
    '&:hover fieldset': { borderColor: BLUE },
    '&.Mui-focused fieldset': { borderColor: BLUE },
  },
  // helper plus compact
  '& .MuiFormHelperText-root': { marginTop: 0.25 },
};

export default function LoginModal({
  open,
  onClose,
  onLoggedIn,
  onOpenRegister,
  onOpenForgot,
  forceRedirect = false,
}) {
  const { t, register, lang, setLang } = useI18n();
  React.useEffect(() => { register('login', loginI18n); }, [register]);

  const nav = useNavigate();
  const location = useLocation();

  const [form, setForm] = React.useState({ email: '', password: '' });
  const [fieldErr, setFieldErr] = React.useState({ email: '', password: '' });
  const [err, setErr] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [langAnchor, setLangAnchor] = React.useState(null);
  const [toastOpen, setToastOpen] = React.useState(false);

  const currentLang = LANGS.find(l => l.code === lang) || LANGS[0];

  const handleChange = (e) => {
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
    setFieldErr((fe) => ({ ...fe, [e.target.name]: '' }));
    if (err) setErr('');
  };

  const validate = () => {
    const fe = {};
    const email = (form.email || '').trim().toLowerCase();
    if (!email) fe.email = t('login.errors.emailRequired');
    else if (!/.+@.+\..+/.test(email)) fe.email = t('login.errors.emailInvalid');
    if (!form.password) fe.password = t('login.errors.passwordRequired');
    setFieldErr(fe);
    return Object.keys(fe).length === 0;
  };

  const formatServerError = (error) => {
    const status = error?.response?.status;
    const rawMsg = error?.response?.data?.message ?? '';
    const msg = String(rawMsg).toLowerCase();

    if (msg.includes('account disabled')) return t('login.errors.disabled');
    if (msg.includes('invalid credentials')) return t('login.errors.invalid');

    if (status === 400) {
      if (msg.includes('email and password')) return t('login.errors.requiredBoth');
      return t('login.errors.invalid');
    }
    if (status === 401 || status === 403) {
      if (msg.includes('invalid') || msg.includes('unauthorized')) return t('login.errors.invalid');
      return t('login.errors.forbidden');
    }
    if (status === 422) return t('login.errors.invalid');
    if (status === 429) return t('login.errors.throttled');
    if (typeof status === 'number' && status >= 500) return t('login.errors.server');
    if (!status) return t('login.errors.offline');
    return t('login.errors.invalid');
  };

  const autoRedirect = (data) => {
    const role = data?.user?.role;
    const redirectTo = location.state?.redirectTo;
    const pendingSummary = location.state?.pendingSummary;

    let stored = null;
    try { stored = JSON.parse(sessionStorage.getItem('redirectAfterAuth') || 'null'); } catch {}
    const target = redirectTo || stored?.redirectTo;
    const summary = pendingSummary || stored?.summary;

    if (target) {
      try { sessionStorage.removeItem('redirectAfterAuth'); } catch {}
      return nav(target, { replace: true, state: { summary } });
    }

    if (role === 'admin' || role === 'superadmin') {
      return nav('/admin', { replace: true });
    }
    return nav('/mes-envois', { replace: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!validate()) return;

    setLoading(true);
    try {
      const payload = {
        email: (form.email || '').trim().toLowerCase(),
        password: (form.password || ''),
      };

      const { data } = await api.post('/auth/login', payload);
      saveAuth(data);

      setToastOpen(true);

      const finalize = () => {
        if (onLoggedIn && !forceRedirect) {
          onLoggedIn(data);
          onClose?.();
          return;
        }
        autoRedirect(data);
        onClose?.();
      };

      setTimeout(finalize, 900);
    } catch (error) {
      const message = formatServerError(error);
      setErr(message);

      const rawMsg = error?.response?.data?.message ?? '';
      const msg = String(rawMsg).toLowerCase();
      if (msg.includes('invalid credentials') || message === t('login.errors.invalid')) {
        setFieldErr((fe) => ({ ...fe, password: t('login.errors.invalid') }));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        fullWidth
        maxWidth="sm"
        BackdropProps={{ sx: { backgroundColor: 'rgba(0,0,0,0.35)', backdropFilter: 'blur(4px)' } }}
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* padding réduit pour resserrer l'ensemble */}
          <Paper elevation={0} sx={{ p: { xs: 2.25, sm: 3 }, borderRadius: 0, position: 'relative', overflow: 'hidden' }}>
            <Box sx={{ position: 'absolute', top: 0, left: 0, right: 0, height: 6, bgcolor: BLUE }} />

            <Box sx={{ position: 'absolute', top: 10, right: 10 }}>
              <IconButton
                onClick={(e) => setLangAnchor(e.currentTarget)}
                size="small"
                sx={{ p: 0.5, borderRadius: 2, border: '1px solid #e0e0e0' }}
              >
                <Box component="img" src={currentLang.flag} alt={currentLang.alt} sx={{ height: 18, width: 'auto', mr: 0.5 }} />
                <ArrowDropDownIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              </IconButton>
              <Menu
                anchorEl={langAnchor}
                open={Boolean(langAnchor)}
                onClose={() => setLangAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                {LANGS.map((l) => (
                  <MenuItem
                    key={l.code}
                    onClick={() => { setLang(l.code); setLangAnchor(null); }}
                    selected={l.code === lang}
                    sx={{ minWidth: 48, justifyContent: 'center', py: 0.5 }}
                  >
                    <Box component="img" src={l.flag} alt={l.alt} sx={{ height: 18, width: 'auto' }} />
                  </MenuItem>
                ))}
              </Menu>
            </Box>

            <Typography component="h1" sx={{ fontWeight: 800, fontSize: { xs: 21, md: 24 }, mb: 0.25 }}>
              {t('login.title')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mb: 1.5 }}>
              {t('login.subtitle')}
            </Typography>

            {err && <Alert severity="error" sx={{ mb: 1.25 }}>{err}</Alert>}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              {/* Champs plus compacts */}
              <TextField
                autoFocus
                name="email"
                label={t('login.form.email')}
                type="email"
                fullWidth
                required
                size="small"
                margin="dense"
                value={form.email}
                onChange={handleChange}
                error={Boolean(fieldErr.email)}
                helperText={fieldErr.email || ' '}
                sx={textFieldSx}
                disabled={loading}
              />
              <TextField
                name="password"
                label={t('login.form.password')}
                type="password"
                fullWidth
                required
                size="small"
                margin="dense"
                value={form.password}
                onChange={handleChange}
                error={Boolean(fieldErr.password)}
                helperText={fieldErr.password || ' '}
                sx={textFieldSx}
                disabled={loading}
              />

              {/* lien oublié: discret, rapproché des champs */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 0.5 }}>
                <Link
                  component="button"
                  type="button"
                  underline="hover"
                  onClick={() => onOpenForgot?.()}
                  sx={{ fontWeight: 600, color: BLUE, fontSize: 14 }}
                >
                  {t('login.form.forgot')}
                </Link>
              </Box>

              {/* Bouton principal : Se connecter */}
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
                sx={{
                  mt: 1.5,
                  py: 1.05,
                  fontWeight: 800,
                  textTransform: 'none',
                  bgcolor: BLUE,
                  '&:hover': { bgcolor: BLUE_HOVER }
                }}
              >
                {loading ? t('login.form.loading') : t('login.form.submit')}
              </Button>

              {/* Bouton “Créer un compte” mis en avant */}
              <Button
                onClick={() => onOpenRegister?.()}
                fullWidth
                variant="outlined"
                sx={{
                  mt: 1,
                  py: 1.0,
                  fontWeight: 800,
                  textTransform: 'none',
                  borderWidth: 2,
                  borderColor: BLUE,
                  color: BLUE,
                  '&:hover': {
                    borderWidth: 2,
                    borderColor: BLUE_HOVER,
                    backgroundColor: 'rgba(4,102,200,0.06)',
                  },
                }}
              >
                {t('login.signup.link')}
              </Button>
            </Box>
          </Paper>
        </DialogContent>
      </Dialog>

      <FancyToast
        open={toastOpen}
        onClose={() => setToastOpen(false)}
        kind="success"
        titleKey="fancyToast.login.title"
        subtitleKey="fancyToast.login.subtitle"
        autoHideDuration={1200}
      />
    </>
  );
}
